;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();import { useState, useEffect, useCallback } from 'react';
import { Col, Row, Form, FormFeedback, Label, Input, FormGroup, Alert } from 'reactstrap';
import { MdErrorOutline } from 'react-icons/md';
import { usePathname } from 'next/navigation';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import usePhoneInputLabelLanguage from '../hooks/usePhoneInputLabelLanguage';
import Cta from './CtaBtn';
import validateMail from '../utils/validateEmailAddress';
import { isValidPersonName, NAME_MAX_LENGTH } from '../utils/validateName';
import { logSentryError } from '../utils/logSentry';
import FileUploadButton from './FileUploadButton';
import axiosInstance from '../utils/axios';
import { event } from '../utils/gtag';
import { plausibleEvent } from '../utils/plausibleEvent';
import 'react-phone-number-input/style.css';
import { Dictionaries, Locale } from '../i18n/i18n-config';
import { getI18nUtils } from '../i18n/i18n-utils';

const allowedCvFileTypes = ['.doc', '.docx', '.pdf', '.rtf'];
const MESSAGE_MIN_LENGTH = 10;

function GeneralContactForm({
  compact = false,
  hasMessage = true,
  landscape = false,
  cvUpload = false,
  submitHandler,
  submitting,
  confirmationMessage,
  dictionary,
  locale,
}: {
  compact?: boolean;
  hasMessage?: boolean;
  landscape?: boolean;
  cvUpload?: boolean;
  submitHandler: React.Dispatch<React.SetStateAction<boolean>>;
  submitting: boolean;
  confirmationMessage: keyof Dictionaries | JSX.Element;
  dictionary: Partial<Dictionaries>;
  locale: Locale;
}): JSX.Element {
  const { formatMessage } = getI18nUtils(dictionary);
  const pathnameWithoutLocale = usePathname().slice(3);
  const phoneLabelLanguage = usePhoneInputLabelLanguage();

  const [inputs, setInputs] = useState({
    firstName: {
      type: 'text',
      value: '',
      touched: false,
      valid: false,
      minLength: 2,
      maxLength: NAME_MAX_LENGTH,
      intlId: 'contact-form-firstName',
      order: 1,
      required: true,
    },
    lastName: {
      type: 'text',
      value: '',
      touched: false,
      valid: false,
      minLength: 2,
      maxLength: NAME_MAX_LENGTH,
      intlId: 'contact-form-lastName',
      order: 2,
      required: true,
    },
    email: {
      type: 'email',
      value: '',
      touched: false,
      valid: false,
      intlId: 'contact-form-email',
      order: 3,
      required: true,
    },
    telephone: {
      type: 'tel',
      value: '',
      touched: false,
      valid: false,
      intlId: 'contact-form-phone',
      order: 4,
      required: true,
    },
  });

  const defaultCvInput = { type: 'file', file: null, name: null, size: null };
  type DefaultCvInput = { type: string; file: string | ArrayBuffer | null; name: null | string; size: null | number };

  const [message, setMessage] = useState({
    type: 'textarea',
    value: '',
    touched: false,
    valid: false,
    intlId: 'contact-form-message',
    order: 5,
    required: true,
    placeholder: 'employers-contact-message-label',
  });
  const [cv, setCv] = useState<DefaultCvInput>(defaultCvInput);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [formIsValid, setFormIsValid] = useState<boolean>(false);
  const [fileError, setFileError] = useState<null | string>(null);
  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [completed, setCompleted] = useState(false);
  const app = process.env.NEXT_PUBLIC_APP;

  async function onSubmitHandler(e) {
    e.preventDefault();

    submitHandler(true);
    setErrorMessage(null);
    const { firstName, lastName, email, telephone } = inputs;
    const body = {
      firstName: firstName.value,
      lastName: lastName.value,
      email: email.value,
      telephone: telephone.value,
      cvFile: cv.file,
      cvName: cv.name,
      cvDate: cv.file ? Date.now() : null,
      language: locale,
      app,
      message: hasMessage ? message.value : undefined,
      page: pathnameWithoutLocale,
    };

    try {
      await axiosInstance.post('/contact', body);
      plausibleEvent('contactFormSubmission');
      event({
        action: 'contactFormSubmission',
        category: app,
        label: pathnameWithoutLocale,
        value: body,
      });
      setCompleted(true);
    } catch (error) {
      setErrorMessage(formatMessage({ id: 'internal-server-error' }));
      logSentryError(error);
    }
    submitHandler(false);
  }

  // check validity for every inputType and terms
  const checkFormValidity = useCallback(() => {
    const validForm: boolean =
      Object.keys(inputs).every((input) => inputs[input].valid) &&
      termsAccepted &&
      !fileError &&
      (hasMessage ? message.valid : true);

    setFormIsValid(validForm);
  }, [fileError, inputs, termsAccepted, hasMessage, message.valid]);

  function handleCvUpload(uploadEvent: React.ChangeEvent<HTMLInputElement>) {
    setFileError(null);
    const file = uploadEvent.target.files?.[0];
    const extension = file ? file.name.substring(file.name.lastIndexOf('.')) : null;

    if (file && extension && allowedCvFileTypes.indexOf(extension.toLowerCase()) !== -1) {
      const Reader = new FileReader();
      Reader.onload = (readerEvent) => {
        if (readerEvent.target === null) return;
        setCv({ ...cv, file: readerEvent.target?.result, name: file.name, size: file.size });
      };
      Reader.readAsDataURL(file);
    } else setFileError(formatMessage({ id: 'file-extension-not-allowed-for-upload' }));

    checkFormValidity();
  }

  // validate a specific input
  function isInputValid(input) {
    const { value, required, minLength, maxLength, type } = input;
    if (value === '' && required) return false;

    if (minLength && value.length < minLength) return false;

    if (maxLength && value.length > maxLength) return false;

    if (type === 'email' && !validateMail(value)) return false;

    if (type === 'tel' && !isValidPhoneNumber(value)) return false;

    // enkel voor- en achternaam zijn tekstvelden
    if (type === 'text' && !isValidPersonName(value, minLength)) return false;

    return true;
  }

  // zelfde minimum als de API (trim().min(10))
  const isMessageValid = (value: string) => value.trim().length >= MESSAGE_MIN_LENGTH;

  // validate the form when an input changes
  useEffect(() => {
    checkFormValidity();
  }, [inputs, termsAccepted, checkFormValidity]);

  function inputChangedHandler(e, inputType) {
    setErrorMessage(null);
    const inputCp = { ...inputs[inputType] };
    inputCp.touched = true;
    inputCp.value = inputType === 'telephone' ? e : e.target.value;
    inputCp.valid = isInputValid(inputCp);
    setInputs({ ...inputs, [inputType]: inputCp });
  }

  function inputMessageChangeHandler(e) {
    if (!hasMessage) return;
    setErrorMessage(null);
    setMessage((prevState) => ({ ...prevState, value: e.target.value, touched: true, valid: isMessageValid(e.target.value) }));
  }

  return (
    <>
      {!completed && (
        <Form onSubmit={(e) => onSubmitHandler(e)}>
          <fieldset disabled={submitting} aria-busy={submitting}>
            <Row>
              {Object.keys(inputs)
                // important!:  to avoid an issue with ios, sort the inputs
                .sort((a, b) => (inputs[a].order > inputs[b].order ? 1 : -1))
                .map((inputType, index) => {
                  const { value, touched, minLength, maxLength, valid, required, type, intlId } = inputs[inputType];

                  return (
                    <Col xs={12} md={compact || (landscape && index !== Object.keys(inputs).length - 1) ? 6 : 12} key={inputType}>
                      <FormGroup>
                        <Label className={`text-muted${required ? ' required' : ''}`} for={inputType}>
                          {formatMessage({ id: intlId })}
                        </Label>
                        {inputType === 'telephone' ? (
                          <PhoneInput
                            className={`${touched && valid ? 'is-valid' : ''} ${touched && !valid ? 'is-invalid' : ''}`}
                            id={inputType}
                            name={inputType}
                            defaultCountry="BE"
                            required={required}
                            value={value}
                            labels={phoneLabelLanguage}
                            international
                            countryCallingCodeEditable={false}
                            // value is stored directly in 'e' so 'e' can be undefined
                            onChange={(e) => e && inputChangedHandler(e, inputType)}
                            onBlur={() => checkFormValidity()}
                          />
                        ) : (
                          <Input
                            id={inputType}
                            name={inputType}
                            maxLength={maxLength}
                            minLength={minLength}
                            required={required}
                            value={value}
                            valid={touched && valid}
                            invalid={touched && !valid}
                            type={type}
                            onChange={(e) => inputChangedHandler(e, inputType)}
                            onBlur={() => checkFormValidity()}
                          />
                        )}
                      </FormGroup>
                    </Col>
                  );
                })}
            </Row>
            {hasMessage && (
              <Row className="mt-2">
                <Col xs={12}>
                  <FormGroup>
                    <Input
                      id={'message'}
                      name={'message'}
                      minLength={MESSAGE_MIN_LENGTH}
                      required={true}
                      value={message.value}
                      valid={message.valid}
                      invalid={message.touched && !message.valid}
                      type={'textarea'}
                      onChange={(e) => inputMessageChangeHandler(e)}
                      onBlur={() => checkFormValidity()}
                    />
                  </FormGroup>
                </Col>
              </Row>
            )}
            {cvUpload && (
              <Row className="mt-2">
                <Col xs="12" md="6">
                  <FormGroup>
                    <FileUploadButton
                      dictionary={dictionary}
                      changed={(e) => handleCvUpload(e)}
                      fileTypes={allowedCvFileTypes.join(',')}
                      id="cvUpload"
                      name="cvUpload"
                      labelName="apply-form-filetitle"
                      invalid={!!fileError}
                      valid={Boolean(cv.file && !fileError)}
                    />
                    <FormFeedback>{formatMessage({ id: 'file-extension-not-allowed-for-upload' })}</FormFeedback>
                  </FormGroup>
                </Col>
              </Row>
            )}
            <Row>
              <Col xs={12}>
                <FormGroup check inline>
                  <Input
                    className="border border-danger"
                    type="checkbox"
                    id="terms"
                    name="terms"
                    onChange={() => {
                      setTermsAccepted(!termsAccepted);
                    }}
                    checked={termsAccepted}
                  />
                  <Label for="terms" check>
                    {formatMessage(
                      { id: 'accept-privacy-terms' },
                      {
                        privacyLink: (
                          <a href={`/${locale}/privacy`} target="_blank" rel="noopener noreferrer">
                            {formatMessage({ id: 'privacy_policy' })}
                          </a>
                        ),
                        termsLink: (
                          <a href={`/${locale}/termsandconditions`} target="_blank" rel="noopener noreferrer">
                            {formatMessage({ id: 'termsandconditions' })}
                          </a>
                        ),
                      }
                    )}
                  </Label>
                </FormGroup>
              </Col>
            </Row>
            {errorMessage && (
              <Alert color="danger" className="d-flex align-items-center py-2">
                <MdErrorOutline className="d-inline-block me-2" size="16" color="#842029" />
                <span>{errorMessage}</span>
              </Alert>
            )}
            <Cta className="cta red noArrow mt-2" type="submit" disabled={!formIsValid}>
              {formatMessage({ id: 'general-contact-form-submit' })}
            </Cta>
          </fieldset>
        </Form>
      )}
      {completed &&
        !errorMessage &&
        (typeof confirmationMessage === 'string' ? <p>{formatMessage({ id: confirmationMessage })}</p> : confirmationMessage)}
    </>
  );
}

export default GeneralContactForm;
